/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import TrailersService from 'api/trailers/TrailersService';
import { TrailerDto } from 'api/trailers/models/TrailerDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './TrailerScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import SearchableSelect from 'common/components/searchableSelect/SearchableSelect';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import TagsService from 'api/tags/TagsService';

type Props = {
};

const TrailerScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasTrailersWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['MY_TRAILERS_WRITE']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<TrailerDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<TrailerDto>();
    const [tags, setTags] = useState<SelectValueLabel[]>([]);
    const [refresh, setRefresh] = useState<number>(1);
    const [formData, setFormData] = useState({
        id: '',
        licensePlate: '',
        designation: '',
        companyId: '',
        tagId: '',
        rowVersion: ''
    });

    const getData = async () => {
        try {
            let result: any = { id: '', licensePlate: '', companyId: '' };
            Loading.show();
            const tags = await TagsService.getMyTags();
            setTags(tags);
            if (id) {
                result = await TrailersService.getById(id);
            }
            setItem(result);
            setFormData(result);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get trailer information', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setRefresh(refresh + 1);
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/trailer/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/trailers');
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }

        try {
            await TrailersService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete trailer', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: TrailerDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: TrailerDto = {
                id: formData.id,
                licensePlate: formData.licensePlate,
                designation: formData.designation,
                tagId: formData.tagId,
                rowVersion: formData.rowVersion
            };
            if (model && model.id) {
                await TrailersService.update(model)
                navigateTo('details', id);
            } else if (model) {
                const id = await TrailersService.create(model)
                navigateTo('details', id);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            if (error?.response?.status === 409) {
                toast(t('messages.trailer_already_exists'));
            } else {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the trailer with id: ${form.id}`, error);
                toast.error(t('messages.error_load_info'));
            }
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('trailers.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('trailers.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
            <Form onSubmit={onSubmit} noValidate validated={validated} key={refresh}>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="licensePlate">
                            <Form.Label>{t('trailers.license_plate')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="licensePlate" type="text" defaultValue={item?.licensePlate} readOnly={isDetails} onChange={handleChange} placeholder={t('trailers.license_plate')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="designation">
                            <Form.Label>{t('trailers.designation')}{!isDetails ? '*' : ''}</Form.Label>
                            <Form.Control required name="designation" type="text" defaultValue={item?.designation} readOnly={isDetails} onChange={handleChange} placeholder={t('trailers.designation')} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="tagId">
                            <Form.Label>{t('trailers.tag')}</Form.Label>
                            <SearchableSelect
                                readOnly={isDetails}
                                name="tagId"
                                value={formData.tagId}
                                options={tags}
                                placeholder={t('common.select_option') ?? ''}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <div className={styles.buttonsContainer}>
                    <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                        {t('common.cancel')}
                    </Button>
                    {isDetails && hasTrailersWritePolicy &&
                        <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as TrailerDto)} >
                            {t('common.delete')}
                        </Button>
                    }
                    {isDetails && hasTrailersWritePolicy &&
                        <Button variant="warning" type="button" className={styles.button} onClick={() => navigateTo('edit', item?.id) }>
                            {t('common.edit')}
                        </Button>
                    }
                    {!isDetails && hasTrailersWritePolicy &&
                        <Button variant="primary" type="submit" className={styles.button}>
                            {t('common.save')}
                        </Button>
                    }
                </div>
            </Form>
            </div>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.licensePlate ?? '' })} />
        </ScreenTitle>
    );
};

export default TrailerScreen;
